import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import authService from '@/services/auth.service';
import { notificationService } from '@/services/notification.service';
import { oneSignalService } from '@/services/onesignal.service';
import { businessService } from '@/services/business.service';
import {
  notificationSocketService,
  NotificationSocketCallbacks,
} from '@/services/notification-socket.service';
import {
  useNotificationStore,
  useUnreadCount,
  useRecentNotifications,
  useNotificationConnection,
  useNotificationLoading,
  useNotificationDropdown,
} from '@/stores/notification.store';
import {
  UserNotification,
  NotificationQueryParams,
  CreateNotificationRequest,
  UpdatePreferencesRequest,
} from '@/types/notification.types';
import toast from 'react-hot-toast';

// ============== NOTIFICATION TOAST ==============

function showNotificationToast(notification: { title: string; message?: string; action_url?: string | null }) {
  toast.custom(
    (t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} w-[420px] bg-white shadow-2xl rounded-xl pointer-events-auto ring-1 ring-black/5 overflow-hidden`}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-lg">🔔</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-snug">{notification.title}</p>
              {notification.message && (
                <p className="mt-1 text-sm text-gray-500 line-clamp-2">{notification.message}</p>
              )}
              {notification.action_url && (
                <button
                  onClick={() => { toast.dismiss(t.id); window.location.href = notification.action_url!; }}
                  className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  View details →
                </button>
              )}
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    ),
    { duration: Infinity, position: 'top-right' }
  );
}

// ============== MAIN HOOK ==============

// Module-level guard to prevent double initialization across multiple component instances
let isGlobalInitialized = false;

// Pending socket-revive timer. Module-level for the same reason as the guard
// above: several components use this hook, and only one revive should be in
// flight at a time.
let socketReviveTimer: ReturnType<typeof setTimeout> | null = null;

// Backoff for the revive loop: quick on a brief drop, then easing off so a
// genuinely dead server is not hit every few seconds by every open tab.
// Reset on a successful connect. Socket.IO's own reconnection (10 attempts,
// 1-5s backoff) still runs underneath for the drops it does retry, and
// reconnect() no-ops while the socket is already up, so the two do not fight.
const SOCKET_REVIVE_MIN_DELAY_MS = 2_000;
const SOCKET_REVIVE_MAX_DELAY_MS = 60_000;
let socketReviveDelayMs = SOCKET_REVIVE_MIN_DELAY_MS;

function resetSocketReviveBackoff(): void {
  socketReviveDelayMs = SOCKET_REVIVE_MIN_DELAY_MS;
}

// The setup effect deliberately has no cleanup (see its closing comment), so the
// window listeners are held here for the logout effect to detach.
let socketReviveListeners: { online: () => void; visibility: () => void } | null = null;

function detachSocketReviveListeners(): void {
  if (!socketReviveListeners) return;
  window.removeEventListener('online', socketReviveListeners.online);
  document.removeEventListener('visibilitychange', socketReviveListeners.visibility);
  socketReviveListeners = null;
}

function cancelSocketRevive(): void {
  if (socketReviveTimer) {
    clearTimeout(socketReviveTimer);
    socketReviveTimer = null;
  }
}

export function useNotifications() {
  const { user, isAuthenticated } = useAuth();

  const {
    notifications,
    unreadCount,
    totalCount,
    hasMore,
    preferences,
    isConnected,
    connectionStatus,
    isLoading,
    isLoadingMore,
    error,
    currentParams,
    setNotifications,
    addNotification,
    appendNotifications,
    markAsRead,
    markAllAsRead: storeMarkAllAsRead,
    removeNotification,
    setUnreadCount,
    setPreferences,
    setConnectionStatus,
    setIsLoading,
    setIsLoadingMore,
    setError,
    setCurrentParams,
    clearNotifications,
  } = useNotificationStore();

  // ============== FETCH NOTIFICATIONS ==============

  const fetchNotifications = useCallback(
    async (_params: NotificationQueryParams = {}) => {
      setIsLoading(true);
      setError(null);

      // Bell dropdown fetch uses the dedicated bell-feed endpoint —
      // only UNREAD + not-cleared items, no pagination.
      const { data, unread_count } = await notificationService.getBellFeed();
      setNotifications(data, data.length);
      setUnreadCount(unread_count);
      setCurrentParams({ skip: 0, limit: data.length });

      setIsLoading(false);
      return {
        success: true,
        code: 200,
        description: '',
        data,
        meta_data: { total: data.length, skip: 0, limit: data.length, unread_count },
      };
    },
    [setNotifications, setUnreadCount, setCurrentParams, setIsLoading, setError]
  );

  // ============== LOAD MORE ==============

  // Bell dropdown no longer paginates — the feed only contains unread+not-cleared
  // items, which is a naturally small set. Kept as a no-op for API compatibility.
  const loadMore = useCallback(async () => {
    return;
  }, []);

  // ============== MARK AS READ ==============

  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      // DB is the source of truth — no optimistic local mutation.
      // Server updates status + unread count; we then refetch.
      notificationSocketService.emitMarkAsRead(notificationId);
      await notificationService.markAsRead(notificationId);
      await fetchNotifications();
    },
    [fetchNotifications]
  );

  // ============== MARK ALL AS READ ==============

  const handleMarkAllAsRead = useCallback(async () => {
    notificationSocketService.emitMarkAllAsRead();
    await notificationService.markAllAsRead();
    await fetchNotifications();
  }, [fetchNotifications]);

  // ============== DISMISS NOTIFICATION ==============
  // Bell popup X button — soft-clear only. Notification stays in DB so the
  // full My Notifications page still shows it. DB tracks is_cleared_from_popup.
  const handleDismiss = useCallback(
    async (notificationId: string) => {
      const success = await notificationService.clearFromPopup(notificationId);
      if (!success) {
        toast.error('Failed to clear notification');
      }
      await fetchNotifications();
    },
    [fetchNotifications]
  );

  // ============== FETCH PREFERENCES ==============

  const fetchPreferences = useCallback(async () => {
    const prefs = await notificationService.getPreferences();
    if (prefs) {
      setPreferences(prefs);
    }
    return prefs;
  }, [setPreferences]);

  // ============== UPDATE PREFERENCES ==============

  const updatePreferences = useCallback(
    async (updates: UpdatePreferencesRequest) => {
      const updated = await notificationService.updatePreferences(updates);
      if (updated) {
        setPreferences(updated);
        toast.success('Preferences updated');
      } else {
        toast.error('Failed to update preferences');
      }
      return updated;
    },
    [setPreferences]
  );

  // ============== FETCH UNREAD COUNT ==============

  const fetchUnreadCount = useCallback(async () => {
    const count = await notificationService.getUnreadCount();
    setUnreadCount(count);
    return count;
  }, [setUnreadCount]);

  // ============== WEBSOCKET SETUP ==============

  // Store refs for callbacks to avoid dependency issues
  const addNotificationRef = useRef(addNotification);
  const markAsReadRef = useRef(markAsRead);
  const markAllAsReadRef = useRef(storeMarkAllAsRead);
  const removeNotificationRef = useRef(removeNotification);
  const setUnreadCountRef = useRef(setUnreadCount);
  const setConnectionStatusRef = useRef(setConnectionStatus);

  // Update refs when functions change
  useEffect(() => {
    addNotificationRef.current = addNotification;
    markAsReadRef.current = markAsRead;
    markAllAsReadRef.current = storeMarkAllAsRead;
    removeNotificationRef.current = removeNotification;
    setUnreadCountRef.current = setUnreadCount;
    setConnectionStatusRef.current = setConnectionStatus;
  });

  useEffect(() => {
    if (!isAuthenticated || !user?.id || isGlobalInitialized) {
      if (!isAuthenticated) console.log('[Notifications] Skipping init - not authenticated');
      else if (!user?.id) console.log('[Notifications] Skipping init - no user ID');
      else if (isGlobalInitialized) console.log('[Notifications] Skipping init - already initialized');
      return;
    }

    console.log('[Notifications] Initializing for user:', user.id);
    isGlobalInitialized = true;

    // Local guard for the current session — prevents double-toast if both
    // WS and poll fire for the same notification before markAsToasted completes.
    const toastedThisSession = new Set<string>();

    // Reusable function to fetch/re-fetch bell feed (unread + not-cleared).
    // Applies to both admin and learner.
    const syncNotifications = async () => {
      try {
        console.log('[Notifications] Syncing bell feed...');
        const { data, unread_count } = await notificationService.getBellFeed();

        // Show toast only for notifications not yet toasted (DB field)
        // and not already toasted this session (local guard for race conditions)
        const newNotifications = data.filter(
          (n) => !n.is_toasted && !toastedThisSession.has(n._id)
        );
        if (newNotifications.length > 0) {
          console.log('[Notifications] New notifications to toast:', newNotifications.length);
          const ids_to_toast: string[] = [];
          for (const notif of newNotifications.slice(0, 3)) {
            showNotificationToast(notif);
            ids_to_toast.push(notif._id);
            toastedThisSession.add(notif._id);
          }
          // Mark remaining un-shown ones as toasted too (>3 cap)
          for (const notif of newNotifications.slice(3)) {
            ids_to_toast.push(notif._id);
            toastedThisSession.add(notif._id);
          }
          // Persist to DB so they never re-toast on refresh/login
          notificationService.markAsToasted(ids_to_toast);
        }

        console.log('[Notifications] Synced', data.length, 'bell items, unread:', unread_count);
        const store = useNotificationStore.getState();
        store.setNotifications(data, data.length);
        store.setUnreadCount(unread_count);
      } catch (error) {
        console.error('[Notifications] Failed to sync notifications:', error);
      }
    };

    /**
     * Bring the socket back while the user is still signed in.
     *
     * Socket.IO retries a transport drop on its own, but not a server-initiated
     * `io server disconnect` and not once its attempts are exhausted — and the
     * init effect cannot re-run because isGlobalInitialized has already
     * latched. Without this the tab stays silent until a reload: both server
     * paths (the direct push and the 15s bell poll) only reach connected
     * sockets, so nothing is queued for a disconnected client.
     */
    const scheduleSocketRevive = () => {
      if (socketReviveTimer) return;

      const delay = socketReviveDelayMs;
      socketReviveDelayMs = Math.min(delay * 2, SOCKET_REVIVE_MAX_DELAY_MS);

      socketReviveTimer = setTimeout(() => {
        socketReviveTimer = null;

        if (!authService.isAuthenticated()) {
          console.log('[Notifications] Socket revive skipped — no session');
          resetSocketReviveBackoff();
          return;
        }
        if (notificationSocketService.isConnected()) {
          resetSocketReviveBackoff();
          return;
        }

        // A hidden tab has nothing to show a toast to, and the visibility
        // listener below revives it the moment it is looked at again.
        if (typeof document !== 'undefined' && document.hidden) {
          console.log('[Notifications] Socket revive deferred — tab hidden');
          scheduleSocketRevive();
          return;
        }

        // Offline: the online listener below fires the moment the network is
        // back, so burning attempts here achieves nothing.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          console.log('[Notifications] Socket revive deferred — offline');
          scheduleSocketRevive();
          return;
        }

        console.log(`[Notifications] Socket still down — reconnecting (next in ${socketReviveDelayMs}ms)`);
        setConnectionStatusRef.current('connecting');
        notificationSocketService.reconnect(user.id, callbacks);

        // Still down on the next window? Try again, further out.
        scheduleSocketRevive();
      }, delay);
    };

    /** Coming back to a tab, or back online, should recover at once. */
    const reviveSocketNow = (trigger: string) => {
      if (!authService.isAuthenticated()) return;
      if (notificationSocketService.isConnected()) return;

      console.log(`[Notifications] Reviving socket immediately — ${trigger}`);
      cancelSocketRevive();
      resetSocketReviveBackoff();
      setConnectionStatusRef.current('connecting');
      notificationSocketService.reconnect(user.id, callbacks);
      scheduleSocketRevive();
    };

    const handleOnline = () => reviveSocketNow('back online');
    const handleVisible = () => {
      if (!document.hidden) reviveSocketNow('tab visible');
    };

    const callbacks: NotificationSocketCallbacks = {
      onNewNotification: (notification: UserNotification) => {
        console.log('[Notifications] New notification received:', notification.title);
        addNotificationRef.current(notification);
        if (!toastedThisSession.has(notification._id)) {
          toastedThisSession.add(notification._id);
          showNotificationToast(notification);
          notificationService.markAsToasted([notification._id]);
        }
      },
      onNotificationRead: (payload) => {
        console.log('[Notifications] Notification read:', payload.notification_id);
        markAsReadRef.current(payload.notification_id);
      },
      onUnreadCountUpdated: (count) => {
        console.log('[Notifications] Unread count updated:', count);
        setUnreadCountRef.current(count);
      },
      onAllRead: () => {
        console.log('[Notifications] All notifications marked read (multi-device sync)');
        markAllAsReadRef.current();
      },
      onNotificationCleared: (notificationId) => {
        console.log('[Notifications] Cleared from popup (multi-device sync):', notificationId);
        removeNotificationRef.current(notificationId);
      },
      onNotificationDismissed: (notificationId) => {
        console.log('[Notifications] Dismissed (multi-device sync):', notificationId);
        removeNotificationRef.current(notificationId);
      },
      onConnect: () => {
        console.log('[Notifications] WebSocket connected');
        cancelSocketRevive();
        resetSocketReviveBackoff();
        setConnectionStatusRef.current('connected');
      },
      onDisconnect: () => {
        console.log('[Notifications] WebSocket disconnected');
        setConnectionStatusRef.current('disconnected');
        scheduleSocketRevive();
      },
      onReconnected: () => {
        console.log('[Notifications] WebSocket reconnected — syncing missed notifications');
        cancelSocketRevive();
        resetSocketReviveBackoff();
        setConnectionStatusRef.current('connected');
        syncNotifications();
      },
      onError: (error) => {
        console.error('[Notifications] Socket error:', error);
        setConnectionStatusRef.current('disconnected');
        scheduleSocketRevive();
      },
      onReconnecting: () => {
        console.log('[Notifications] WebSocket reconnecting...');
        setConnectionStatusRef.current('connecting');
      },
    };

    // Connect to WebSocket
    console.log('[Notifications] Connecting WebSocket for user:', user.id);
    setConnectionStatusRef.current('connecting');
    notificationSocketService.connect(user.id, callbacks);

    // Recover the moment the network or the tab comes back, rather than waiting
    // out the backoff. Left attached for the life of the page, like the socket
    // itself — the logout effect tears both down.
    detachSocketReviveListeners();
    socketReviveListeners = { online: handleOnline, visibility: handleVisible };
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisible);

    // Fetch initial data and initialize OneSignal
    (async () => {
      try {
        // Fetch notifications and unread count
        await syncNotifications();

        // Fetch preferences
        const prefs = await notificationService.getPreferences();
        if (prefs) {
          console.log('[Notifications] Preferences loaded:', { push: prefs.channels?.push?.enabled, email: prefs.channels?.email?.enabled });
          useNotificationStore.getState().setPreferences(prefs);
        }

        // Initialize OneSignal with tenant's app_id
        console.log('[Notifications] Fetching push config...');
        const push_config = await notificationService.getPushConfig();
        console.log('[Notifications] Push config response:', push_config);

        if (!push_config) {
          console.warn('[Notifications] No push config returned - OneSignal will not initialize');
        } else if (!push_config.enabled) {
          console.warn('[Notifications] Push is disabled in config - OneSignal will not initialize');
        } else if (!push_config.app_id) {
          console.warn('[Notifications] No app_id in push config - OneSignal will not initialize');
        } else {
          // Pull company name + collapsed-menu logo so the slidedown
          // prompt matches the rest of the admin/learner UI. Best-effort —
          // if business settings fail to load, fall back to generic copy.
          // OneSignal silently truncates actionMessage past ~90 chars, so
          // keep the copy concise and lead with the company name.
          let actionMessage: string | undefined;
          let icon: string | undefined;
          try {
            const business = await businessService.getBusinessSettings();
            const company_name: string =
              business?.data?.company_name?.trim() ||
              business?.company_name?.trim() ||
              'Skillzen';
            icon = business?.data?.small_logo_url || business?.data?.large_logo_url;
            actionMessage = `Allow ${company_name} to send you push notifications?`;
          } catch (err) {
            console.warn('[Notifications] Could not load business settings for slidedown copy', err);
          }

          console.log('[Notifications] Initializing OneSignal with app_id:', push_config.app_id, 'safari_web_id:', push_config.safari_web_id || 'none');
          const initialized = await oneSignalService.init(
            push_config.app_id,
            push_config.safari_web_id,
            { actionMessage, icon }
          );
          console.log('[Notifications] OneSignal init result:', initialized);

          if (initialized && user?.id) {
            oneSignalService.onForegroundWillDisplay((event: any) => {
              event.preventDefault();
              event.notification.display();
            });

            oneSignalService.onNotificationClick((event: any) => {
              // Do NOT navigate the current tab. The service worker's
              // notificationclick handler opens action_url in a new tab via
              // clients.openWindow(). Doing window.location.href = url here
              // would hijack whatever the user is currently viewing — that
              // was the "current tab navigates away" bug. Keep this handler
              // as a no-op (with a log) for any future analytics or
              // mark-as-read hooks.
              console.log(
                '[OneSignal] Notification clicked (foreground):',
                event?.notification?.title
              );
            });

            oneSignalService.onSubscriptionChange((change: any) => {
              console.log('[OneSignal] Subscription changed:', change);
            });

            await oneSignalService.login(user.id);

            // Recover from stale OneSignal local state.
            // Why: if a previous subscribe attempt half-failed (browser
            // registered the push subscription but OneSignal's backend
            // rejected the update), the SDK stores a subscriptionId locally
            // with an empty token. Every subsequent load PATCHes that broken
            // record (→ 400) instead of creating a fresh subscription, so
            // OneSignal's backend never links this browser to the user's
            // external_id and admin-sent push skips the browser entirely.
            // Detect: permission granted + SDK has subscriptionId + token empty.
            // Fix: wipe OneSignal's SW + IndexedDB and reload — fresh init
            // creates a new subscription record. Browser permission is
            // already granted, so no slidedown re-appears.
            // Guarded by sessionStorage so it can only run once per session.
            try {
              const recovery_key = 'onesignal_stale_state_recovered';
              const has_granted_perm =
                'Notification' in window && Notification.permission === 'granted';
              const os_any = (window as any).OneSignal;
              const sub_id = os_any?.User?.PushSubscription?.id;
              const token = os_any?.User?.PushSubscription?.token;
              const is_stale =
                has_granted_perm && sub_id && (!token || token === '');

              if (is_stale && !sessionStorage.getItem(recovery_key)) {
                console.warn(
                  '[Notifications] Stale OneSignal state detected — clearing and reloading',
                  { sub_id, token_empty: !token }
                );
                sessionStorage.setItem(recovery_key, '1');

                // Unregister OneSignal service worker(s)
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(
                  regs
                    .filter(r =>
                      (r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || '')
                        .toLowerCase()
                        .includes('onesignal')
                    )
                    .map(r => r.unregister())
                );

                // Delete OneSignal IndexedDB databases
                if ('databases' in indexedDB) {
                  const dbs = await (indexedDB as any).databases();
                  await Promise.all(
                    (dbs || [])
                      .filter((db: any) => db?.name?.toLowerCase().includes('onesignal'))
                      .map(
                        (db: any) =>
                          new Promise<void>(resolve => {
                            const req = indexedDB.deleteDatabase(db.name);
                            req.onsuccess = req.onerror = req.onblocked = () => resolve();
                          })
                      )
                  );
                }

                // Reload — OneSignal SDK re-inits fresh, creates new subscription
                // pointing at the browser's existing push endpoint.
                window.location.reload();
                return;
              }
            } catch (err) {
              console.warn('[Notifications] Stale-state recovery failed', err);
            }

            // Edge / restrictive-browser recovery: if browser permission is
            // granted but no PushManager subscription exists, force the SDK
            // to retry optIn() so OneSignal's backend receives a real FCM
            // endpoint for this device. Without this, Edge users who
            // previously had a subscribe-attempt fail silently never get
            // re-subscribed automatically — admin-sent pushes skip them.
            try {
              await oneSignalService.forceSubscribeIfMissing();
            } catch (err) {
              console.warn('[Notifications] forceSubscribeIfMissing failed', err);
            }

            // Sync OneSignal subscription state into our DB.
            // If the user is no longer subscribed anywhere (tokens invalid,
            // admin unsubscribed, user unsubscribed on another device),
            // backend flips channels.push.enabled=false so the Settings
            // toggle + push filtering reflect reality. Non-blocking and
            // guarded — any rejection is logged and swallowed so it can't
            // bubble up as an unhandled rejection.
            (async () => {
              try {
                const result = await notificationService.syncPushState();
                if (!result?.flag_updated) return;
                console.log('[Notifications] Push preference synced to false (user not subscribed on OneSignal)');
                const prefs = await notificationService.getPreferences();
                if (prefs) useNotificationStore.getState().setPreferences(prefs);
              } catch (error) {
                console.warn('[Notifications] syncPushState flow failed', error);
              }
            })();

            // Manual-only subscribe flow:
            // No automatic prompt on login. User enables push explicitly via the
            // Settings page toggle (NotificationPreferences). This avoids
            // duplicate slidedown spam and matches "manual subscribe only" intent.

            console.log('[Notifications] OneSignal fully initialized and ready');
          } else if (!initialized) {
            console.warn('[Notifications] OneSignal init returned false - check environment/CSP');
          }
        }
      } catch (error) {
        console.error('[Notifications] Failed to fetch initial data:', error);
      }
    })();

    // No polling — the WebSocket gateway emits notification:new,
    // notification:read, notification:all-read, notification:cleared,
    // notification:dismissed, and unread-count:updated for every state
    // change that affects the bell feed. On reconnect after a disconnect,
    // syncNotifications() runs once via the onReconnected callback above
    // to catch up on anything missed during downtime.

    // Update callbacks on the socket service
    notificationSocketService.updateCallbacks(callbacks);

    // No cleanup function here.
    // React.StrictMode in dev runs the cleanup once before the real mount,
    // which would tear down the socket we just opened and the second mount
    // doesn't always recover cleanly (subscribe race, server-side close).
    // Instead, the singleton socket stays alive across re-mounts and is
    // disconnected by the logout effect below when isAuthenticated flips.
    // Intentionally NOT calling oneSignalService.logout() either — we want
    // the browser's OneSignal subscription to persist across app logout so
    // backend can still deliver targeted pushes to the user's external_id.
    // When another user logs in on this browser, oneSignalService.login(newUserId)
    // rekeys the subscription.
  }, [isAuthenticated, user?.id]);

  // Disconnect the socket only when the user actually logs out.
  // Decoupled from the setup effect so React's mount-cleanup-mount cycle
  // (StrictMode in dev, parent re-renders, etc.) doesn't kill the connection.
  useEffect(() => {
    if (!isAuthenticated && isGlobalInitialized) {
      console.log('[Notifications] User logged out — disconnecting socket');
      cancelSocketRevive();
      resetSocketReviveBackoff();
      detachSocketReviveListeners();
      notificationSocketService.disconnect();
      isGlobalInitialized = false;
    }
  }, [isAuthenticated]);

  // ============== RETURN VALUES ==============

  return {
    // State
    notifications,
    unreadCount,
    totalCount,
    hasMore,
    preferences,
    isConnected,
    connectionStatus,
    isLoading,
    isLoadingMore,
    error,

    // Actions
    fetchNotifications,
    loadMore,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    dismiss: handleDismiss,
    fetchPreferences,
    updatePreferences,
    fetchUnreadCount,
    clearNotifications,
  };
}

// ============== ADMIN HOOK ==============

export function useAdminNotifications() {
  const sendNotification = useCallback(async (notification: CreateNotificationRequest) => {
    const result = await notificationService.sendNotification(notification);

    if (result.success) {
      toast.success('Notification initiated successfully');
    } else {
      toast.error(result.error || 'Failed to send notification');
    }

    return result;
  }, []);

  const scheduleNotification = useCallback(
    async (notification: CreateNotificationRequest & { scheduled_at: string }) => {
      const result = await notificationService.scheduleNotification(notification);

      if (result.success) {
        toast.success('Notification scheduled successfully');
      } else {
        toast.error(result.error || 'Failed to schedule notification');
      }

      return result;
    },
    []
  );

  const cancelScheduledNotification = useCallback(async (notificationId: string) => {
    const success = await notificationService.cancelScheduledNotification(notificationId);

    if (success) {
      toast.success('Scheduled notification cancelled');
    } else {
      toast.error('Failed to cancel notification');
    }

    return success;
  }, []);

  const retryNotification = useCallback(async (notificationId: string) => {
    const result = await notificationService.retryFailedNotification(notificationId);

    if (result.success) {
      toast.success('Notification retry initiated');
    } else {
      toast.error(result.error || 'Failed to retry notification');
    }

    return result.success;
  }, []);

  const getNotificationHistory = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      type?: string;
      search?: string;
      history_status?: 'SENT' | 'SCHEDULED' | 'CANCELLED' | 'FAILED';
      start_date?: string;
      end_date?: string;
    }) => {
      return notificationService.getNotificationHistory(params);
    },
    []
  );

  const getNotificationMetrics = useCallback(async () => {
    return notificationService.getNotificationMetrics();
  }, []);

  return {
    sendNotification,
    scheduleNotification,
    cancelScheduledNotification,
    retryNotification,
    getNotificationHistory,
    getNotificationMetrics,
  };
}

// ============== CONVENIENCE EXPORTS ==============

export { useUnreadCount, useRecentNotifications, useNotificationConnection, useNotificationLoading, useNotificationDropdown };
